import { Box } from "@mui/material";
import React from "react";
import useTheme from "../../../../../../../common/hooks/useTheme";
import { Theme } from "../../../../../../../providers/ThemeProvider";
import { ScoreLevel } from "../../helpers/mealItemHelpers";

type Props =
  /** White pill over the card image: Swapped / Skipped / Extra / Personalized. */
  | { kind: "status"; label: string }
  /** Meal score band, coloured by level; with `score`, "72% High" as mobile shows it. */
  | { kind: "score"; level: ScoreLevel; score?: number }
  /** Outlined chip: cuisine, diet type, ingredient. */
  | { kind: "content"; label: string };

/** The one pill used by the meal cards and the details panel. */
const PlanTag = (props: Props) => {
  const theme: Theme = useTheme();

  if (props.kind === "score") {
    const label =
      props.score === undefined
        ? props.level
        : `${Math.round(props.score)}% ${props.level}`;
    return <Box sx={styles.pill(scoreColors(theme, props.level))}>{label}</Box>;
  }

  const colors =
    props.kind === "status"
      ? {
          background: theme.colors_ds.NEUTRAL_BG_MAIN,
          text: theme.colors_ds.NEUTRAL_TEXT_STRONG,
          border: theme.colors_ds.NEUTRAL_BORDER_WEAK,
        }
      : {
          background: "transparent",
          text: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
          border: theme.colors_ds.NEUTRAL_BORDER_WEAK,
        };

  return <Box sx={styles.pill(colors)}>{props.label}</Box>;
};

export default PlanTag;

type PillColors = { background: string; text: string; border: string };

/** Mobile uses red / amber / green for Low / Moderate / High. */
const scoreColors = (theme: Theme, level: ScoreLevel): PillColors => {
  switch (level) {
    case "Low":
      return {
        background: theme.colorTokens.D[10],
        text: theme.colorTokens.D[70],
        border: theme.colorTokens.D[10],
      };
    case "Moderate":
      return {
        background: theme.colorTokens.W[10],
        text: theme.colorTokens.W[80],
        border: theme.colorTokens.W[10],
      };
    case "High":
    default:
      return {
        background: theme.colorTokens.S[10],
        text: theme.colorTokens.S[70],
        border: theme.colorTokens.S[10],
      };
  }
};

const styles = {
  pill: (colors: PillColors) => ({
    display: "inline-flex",
    alignItems: "center",
    padding: "2px 8px",
    borderRadius: "12px",
    border: `1px solid ${colors.border}`,
    backgroundColor: colors.background,
    color: colors.text,
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "16px",
    whiteSpace: "nowrap" as "nowrap",
  }),
};
